import { useState } from 'react'
import {
  Box,
  Text,
  Badge,
  Table,
  IconButton,
  HStack,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Portal,
} from '@chakra-ui/react'
import { Truck, MoreVertical, Edit, Trash2, Phone, Mail, ExternalLink } from 'lucide-react'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { useDeleteSupplier } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { SupplierWithCategory, VatRegime } from '../types'
import { VAT_REGIME_LABELS } from '../types'

interface SuppliersListProps {
  suppliers?: SupplierWithCategory[]
  searchTerm: string
  onEdit: (supplier: SupplierWithCategory) => void
  onView: (supplier: SupplierWithCategory) => void
  isLoading?: boolean
}

export function SuppliersList({
  suppliers,
  searchTerm,
  onEdit,
  onView,
  isLoading,
}: SuppliersListProps) {
  const deleteSupplier = useDeleteSupplier()
  const [supplierToDelete, setSupplierToDelete] = useState<SupplierWithCategory | null>(null)

  const filtered = suppliers?.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.siret && s.siret.includes(searchTerm)) ||
      (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleDelete = async () => {
    if (!supplierToDelete) return

    try {
      await deleteSupplier.mutateAsync(supplierToDelete.id)
      toaster.create({
        title: 'Succès',
        description: 'Fournisseur supprimé',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la suppression',
        type: 'error',
      })
    } finally {
      setSupplierToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <Text color="gray.500">Chargement des fournisseurs...</Text>
      </Box>
    )
  }

  if (!filtered || filtered.length === 0) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <Truck size={48} color="var(--chakra-colors-gray-300)" style={{ margin: '0 auto' }} />
        <Text mt={4} color="gray.500">
          {searchTerm ? 'Aucun fournisseur trouvé' : 'Aucun fournisseur enregistré'}
        </Text>
        <Text fontSize="sm" color="gray.400">
          Cliquez sur "Nouveau fournisseur" pour en ajouter un
        </Text>
      </Box>
    )
  }

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader w="100px">Code</Table.ColumnHeader>
              <Table.ColumnHeader>Raison sociale</Table.ColumnHeader>
              <Table.ColumnHeader>Catégorie</Table.ColumnHeader>
              <Table.ColumnHeader>Contact</Table.ColumnHeader>
              <Table.ColumnHeader>Régime TVA</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Statut</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" textAlign="right">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((supplier) => (
              <Table.Row key={supplier.id} _hover={{ bg: 'gray.50' }} cursor="pointer">
                <Table.Cell onClick={() => onView(supplier)}>
                  <Text fontFamily="mono" fontWeight="medium" color="brand.600">
                    {supplier.code}
                  </Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(supplier)}>
                  <Text fontWeight="medium">{supplier.name}</Text>
                  {supplier.trade_name && (
                    <Text fontSize="sm" color="gray.500">
                      {supplier.trade_name}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(supplier)}>
                  {supplier.supplier_categories ? (
                    <Badge colorPalette="blue" size="sm">
                      {supplier.supplier_categories.name}
                    </Badge>
                  ) : (
                    <Text color="gray.400">-</Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(supplier)}>
                  {supplier.email || supplier.phone ? (
                    <HStack gap={2} flexWrap="wrap">
                      {supplier.email && (
                        <HStack gap={1} color="gray.600" fontSize="sm">
                          <Mail size={14} />
                          <Text>{supplier.email}</Text>
                        </HStack>
                      )}
                      {supplier.phone && (
                        <HStack gap={1} color="gray.600" fontSize="sm">
                          <Phone size={14} />
                          <Text>{supplier.phone}</Text>
                        </HStack>
                      )}
                    </HStack>
                  ) : (
                    <Text color="gray.400">-</Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(supplier)}>
                  <Text fontSize="sm">{VAT_REGIME_LABELS[supplier.vat_regime as VatRegime]}</Text>
                </Table.Cell>
                <Table.Cell textAlign="center" onClick={() => onView(supplier)}>
                  <Badge colorPalette={supplier.is_active ? 'green' : 'gray'} size="sm">
                    {supplier.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <MenuRoot positioning={{ placement: 'bottom-end' }}>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="160px">
                          <MenuItem value="view" onClick={() => onView(supplier)}>
                            <ExternalLink size={16} />
                            <Text ml={2}>Voir le détail</Text>
                          </MenuItem>
                          <MenuItem value="edit" onClick={() => onEdit(supplier)}>
                            <Edit size={16} />
                            <Text ml={2}>Modifier</Text>
                          </MenuItem>
                          <MenuItem
                            value="delete"
                            color="red.500"
                            onClick={() => setSupplierToDelete(supplier)}
                          >
                            <Trash2 size={16} />
                            <Text ml={2}>Supprimer</Text>
                          </MenuItem>
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      <ConfirmDialog
        isOpen={!!supplierToDelete}
        onClose={() => setSupplierToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le fournisseur"
        message={`Êtes-vous sûr de vouloir supprimer le fournisseur "${supplierToDelete?.name}" ? Cette action est irréversible et supprimera également tous les contrats, factures et règlements associés.`}
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteSupplier.isPending}
      />
    </>
  )
}
